// /apps — the project's published apps, now FIRST-CLASS objects.
//
// An app is a *.view Flow endpoint promoted to a real object (backend apps
// registry): it has an owner, a display title (renameable), Drive-style
// folders, and a sharing model — private, shared to specific org members, or
// visible to the whole organization by link. This page is the Drive-like
// explorer over them: a left rail with "My apps" (personal folder tree),
// "Shared with me" and "Organization" sections, plus per-app rename / move /
// share / remove. Legacy views (created before the registry) appear under
// "Organization" — everyone in the org keeps seeing them, unchanged.
//
// The rendering mechanics are untouched: clicking an app opens it ONCE on the
// right, embedded the same way the Flow studio embeds (an iframe to the bare
// /flow-embed canvas with ?autorunView=1). The user must never see the
// underlying flow pipeline graph: a loader covers the iframe until the view's
// React root has mounted inside it. Sharing shares ONLY the app surface —
// never the sources, recipe, or flow graph behind it.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  AppWindow, Loader2, RefreshCw, Share2, PanelLeftClose, PanelLeftOpen,
  Folder, FolderOpen, FolderPlus, MoreVertical, Package, Pencil, Trash2, ExternalLink,
  Globe, Lock, Users, Link as LinkIcon, Check, X, ChevronRight, ChevronDown,
  UserRound, ClipboardList, Sparkles, Bug, Wrench, Bot,
  MessageSquare, Workflow, AlertTriangle, History, Copy,
  Eye, GraduationCap, ShieldCheck, GitBranch, BookText,
} from "lucide-react";
import { REQUEST_KIND_META } from "@/components/board/requestMeta";
import {
  listApps, resolveApp, updateApp, removeApp, duplicateApp, shareApp, unshareApp,
  listOrgMembers, createAppFolder, updateAppFolder, deleteAppFolder,
  listAppRequests, VIEWER_CHROME_DEFAULTS,
} from "../services/apps";
import { loadProjectGuides } from "../components/tutorial/guides";
import { wfEngine } from "../services/workflows";
import { exportTemplate } from "../services/templates";
import { useAuth } from "../contexts/AuthContext";
import { createProject } from "../services/projects";
import { createPageUrl } from "@/utils";
import { isPortal, portalConfig } from "../utils/portal";
import { useProject } from "@/contexts/ProjectContext";
import AppRequestDialog from "../components/AppRequestDialog";
import VersionHistory from "../components/VersionHistory";

// Turn "cortex.customers.dashboard.view" into "Customers Dashboard".
function viewTitle(name) {
  let n = String(name || "");
  n = n.replace(/\.view$/, "");
  const parts = n.split(".");
  if (parts[0] === "cortex") parts.shift();
  const words = parts.join(" ").replace(/[_-]+/g, " ").trim();
  return words.replace(/\b\w/g, (c) => c.toUpperCase()) || name;
}

// Standalone, shareable URL for a view: the bare /flow-embed render (no cortex
// chrome). `deepLink` is forwarded as the iframe URL HASH so the embedded app
// can route to a specific page/record from the outer URL.
function viewShareUrl(flowProjectId, name, deepLink) {
  if (!flowProjectId || !name) return null;
  const base = `/flow-embed/projects/${flowProjectId}/endpoints/${encodeURIComponent(name)}/flow?autorunView=1`;
  const hash = (deepLink || "").replace(/^[#?]/, "");
  return hash ? `${base}#${hash}` : base;
}

// ── Small modal shell (matches the plain-div dialog style used elsewhere) ────
function Modal({ title, onClose, children, width = 440 }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* Header pinned, BODY scrolls: a long child (version history) used to push
          the title out of the scroll viewport, so the close button went with it. */}
      <div
        className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5 flex flex-col"
        style={{ width, maxWidth: "calc(100vw - 32px)", maxHeight: "85vh" }}
      >
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="font-semibold text-page-text truncate pr-2">{title}</div>
          <button type="button" onClick={onClose} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1 shrink-0">
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto min-h-0">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-card-border bg-page-bg px-3 py-2 text-sm text-page-text outline-none focus:border-nav-active";
const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30";

export default function Apps() {
  const { viewName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectSlug, branch, sharedSurfaceOnly, projectsLoading } = useProject();
  // Someone who reaches this project only through a shared app/agent cannot
  // build or request anything in it (the request would 403), so don't offer to.
  const canBuild = !isPortal() && !sharedSurfaceOnly;
  const deepLink = (() => {
    try { const sp = new URLSearchParams(location.search || ""); sp.delete("project"); return sp.toString(); }
    catch { return (location.search || "").replace(/^\?/, ""); }
  })();

  const [apps, setApps] = useState([]);
  const [folders, setFolders] = useState([]);
  const [me, setMe] = useState(null);
  const [flowProjectId, setFlowProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Deep link to an app that isn't in the caller's list: resolve it (it may be
  // freshly created); a 403 renders the "not shared with you" state.
  const [denied, setDenied] = useState(null);
  const [viewReady, setViewReady] = useState(false);
  // Loader-gate failure state (see the poll below): the embed either never
  // mounted within the timeout, or the studio told us the socket was refused.
  // `viewAttempt` bumps on Try again and is folded into the iframe key so the
  // frame genuinely REMOUNTS — re-running only the poll would watch a frame
  // that already failed.
  const [viewTimedOut, setViewTimedOut] = useState(false);
  const [viewDenied, setViewDenied] = useState(null);
  const [viewAttempt, setViewAttempt] = useState(0);
  const iframeRef = useRef(null);
  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("appsRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => { const n = !o; try { localStorage.setItem("appsRailOpen", n ? "1" : "0"); } catch {} return n; });


  // Collapsed folders (ids). Default: everything expanded.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleFolder = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Drag & drop: drag an OWNED app row onto a folder row (or the "My apps"
  // section = root) to file it. dragAppId is the app being dragged; dropTarget
  // is the hovered target ('root' or a folder id) for highlight.
  const [dragAppId, setDragAppId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Per-app "⋯" menu + dialogs.
  const [menuAppId, setMenuAppId] = useState(null);
  const [menuFolderId, setMenuFolderId] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);       // app
  const [copyTarget, setCopyTarget] = useState(null);           // app — duplicate
  const [moveTarget, setMoveTarget] = useState(null);           // app
  const [shareTarget, setShareTarget] = useState(null);         // app
  const [historyTarget, setHistoryTarget] = useState(null);     // app — version history
  const [viewerOptionsTarget, setViewerOptionsTarget] = useState(null); // app — share-page chrome
  const [removeTarget, setRemoveTarget] = useState(null);       // app
  const [folderDialog, setFolderDialog] = useState(null);       // {mode:'create'|'rename', folder?}
  const [busy, setBusy] = useState(false);
  const [dialogErr, setDialogErr] = useState(null);
  // App requests: the "get an agent to build/fix an app" queue. The list of
  // requests itself lives on the Board page; the rail button links there.
  const [requestDialog, setRequestDialog] = useState(null);     // {kind, app?}
  // Publish an app into the App Templates library. A template is a
  // platform-wide artifact, so the act is platform_admin's — from ANY project,
  // not only from the project that happens to store the library
  // (docs/app-templates.md §4.1). /api/tpl/export is server-enforced
  // regardless; hiding the item is purely presentational.
  const { user } = useAuth();
  const isPlatformAdmin = !!user?.orgs?.some((o) => o.role === "platform_admin");
  const [publishTarget, setPublishTarget] = useState(null);      // app — publish as template
  // Open (queued/claimed/running) request count for the rail badge.
  const [openRequests, setOpenRequests] = useState(0);
  useEffect(() => {
    // Only for someone who can actually build. A share-only viewer has no
    // request rail to badge and is 403'd on /api/app_requests, so this could
    // only ever put a refusal in their console.
    //
    // The projectsLoading wait is the load-bearing half: sharedSurfaceOnly is
    // false until GET /api/projects lands, so firing on the first render would
    // send the request BEFORE we know whom we are rendering for — the refusal
    // is already in the console by the time canBuild turns false.
    if (projectsLoading) return;
    if (!canBuild) { setOpenRequests(0); return; }
    let alive = true;
    listAppRequests({ status: "open" })
      .then((r) => { if (alive) setOpenRequests((r?.requests || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canBuild, projectsLoading]);

  // PORTAL (standalone deployment): the Apps list is exactly the prebuilt
  // sites that shipped with THIS deployment — each on its own domain. No
  // cortex registry, no RBAC filtering, no live view re-runs.
  const prettyModule = (m) =>
    String(m || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  const load = () => {
    if (isPortal()) {
      const views = portalConfig()?.views || [];
      setApps(views.map((v) => ({
        id: v.module,
        view_name: v.module,
        title: prettyModule(v.module),
        visibility: "org",
        portal_url: v.url,
      })));
      setFolders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    listApps()
      .then((r) => {
        setApps(r?.apps || []);
        setFolders(r?.folders || []);
        setMe(r?.me || null);
        setFlowProjectId(r?.flow_project_id || null);
      })
      .catch((e) => setErr(e?.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Close any open row menu on outside click.
  useEffect(() => {
    if (menuAppId == null && menuFolderId == null) return;
    const close = () => { setMenuAppId(null); setMenuFolderId(null); };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuAppId, menuFolderId]);

  const selected = useMemo(() => {
    if (!viewName) return null;
    return apps.find((a) => a.view_name === viewName) || { view_name: viewName, title: null, unresolved: true };
  }, [viewName, apps]);

  // Mobile: an open app takes the full width — collapse the rail while an app
  // is selected and bring it back when nothing is (the 40px reopen strip
  // stays available either way). Desktop keeps the user's persisted choice.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia("(max-width: 767px)").matches) return;
    setRailOpen(!selected);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // A deep link to an app outside the visible list: access-check it.
  useEffect(() => {
    setDenied(null);
    if (!viewName || loading) return;
    if (apps.some((a) => a.view_name === viewName)) return;
    let alive = true;
    resolveApp(viewName)
      .then((r) => { if (alive && r?.app) setApps((prev) => prev.some((a) => a.id === r.app.id) ? prev : [...prev, r.app]); })
      .catch((e) => { if (alive) setDenied(e?.message || "This app has not been shared with you."); });
    return () => { alive = false; };
  }, [viewName, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayTitle = (a) => a?.title || viewTitle(a?.view_name);

  // Stable iframe identity: project+view only (NO deepLink), so mirroring the
  // outer URL as the user navigates does NOT remount/re-run the iframe.
  const embedKey = viewShareUrl(flowProjectId, selected?.view_name);
  // Companion toggles on the IN-CORTEX embed — the SAME two buttons a deployed
  // standalone app shows, so this preview looks identical to the deployment.
  // (Offset right:76 here: the platform's global chat launcher owns the
  // bottom-right corner; on a deployed page the companions sit at right:20.)
  // Companions over an open app are the speed dial's job now (AssistantChat,
  // bottom-right): Chat, Workflows (data-driven), Pipeline, Governance — one
  // control. Deployed apps (portal_url) carry theirs INSIDE the iframe.
  const initialDeepLinkRef = useRef(deepLink);
  const lastKeyRef = useRef(embedKey);
  if (lastKeyRef.current !== embedKey) { lastKeyRef.current = embedKey; initialDeepLinkRef.current = deepLink; }
  // Portal apps live on their OWN login-gated domains. The iframe (and
  // open-in-tab) hand the current portal session over via a #token= fragment —
  // the viewer stores it and strips the hash immediately, so the user isn't
  // asked to sign in twice. Copied share links stay BARE: recipients log in.
  const portalHandoff = (url) => {
    if (!url) return url;
    try {
      const tok = localStorage.getItem("access_token");
      return tok ? `${url}#token=${encodeURIComponent(tok)}` : url;
    } catch { return url; }
  };
  const embedSrc = denied ? null : (selected?.portal_url ? portalHandoff(selected.portal_url) : viewShareUrl(flowProjectId, selected?.view_name, initialDeepLinkRef.current));

  const openView = (name) => {
    if (selected?.view_name === name) return;
    navigate(createPageUrl(`apps/${encodeURIComponent(name)}`));
  };

  // "Ask agent" (agent-backed apps only): open the project's orchestrator chat,
  // where this app's agent is available via the badge/delegates. The chat is
  // mounted globally (Layout) and binds to the active project from the URL, so
  // we just fire the same `cortex:open-harness` event the strategy-wizard uses.
  // Prefill is a nicety only — landing in the orchestrator with the badge is the
  // requirement for this phase; no deep-link plumbing is added.
  const askAgent = (app) => {
    window.dispatchEvent(new CustomEvent("cortex:open-harness", {
      detail: {
        harness: "orchestrator",
        initialPrompt: `Use the ${displayTitle(app)} agent: `,
      },
    }));
  };

  // "Add agent" (agent-less apps, i.e. !app.has_agent): a backing agent is
  // OPT-IN per app. This files a normal `change` app-request whose title/details
  // match the implement-app-request trigger heuristic verbatim, so the local
  // agent authors the agent manifest for the existing view (without touching
  // the view). Reuses the same RequestDialog / POST /api/app_requests machinery
  // as "Request a change" — just prefilled.
  const addAgent = (app) => {
    setRequestDialog({
      kind: "change",
      app,
      title: "Add a backing agent",
      details:
        "Add a backing agent to this app (agent-backed): author its cortex.agent manifest from the view's endpoint surface per docs/project-agents.md; do not change the view.",
    });
  };

  const shareLink = (app) => {
    const dl = selected?.view_name === app.view_name ? deepLink : "";
    const sharePath = createPageUrl(`share/apps/${encodeURIComponent(app.view_name)}`, { slug: projectSlug, branch });
    return window.location.origin + sharePath + (dl ? `?${dl}` : "");
  };
  const openInNewTab = (app) =>
    window.open(app.portal_url ? portalHandoff(app.portal_url) : shareLink(app), "_blank", "noopener,noreferrer");

  // ── Two-way route bridge with the embedded view (unchanged mechanics) ──────
  const lastBridgedRef = useRef(null);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin && e.origin !== window.location.origin) return;
      const d = e.data;
      if (!d || d.source !== "cortex-view-route" || !selected?.view_name) return;
      const hash = String(d.hash || "").replace(/^#/, "");
      if (hash === lastBridgedRef.current) return;
      lastBridgedRef.current = hash;
      const qs = new URLSearchParams(hash).toString();
      const base = createPageUrl(`apps/${encodeURIComponent(selected.view_name)}`, { slug: projectSlug, branch });
      navigate(base + (qs ? `?${qs}` : ""), { replace: true });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [selected?.view_name, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (deepLink === lastBridgedRef.current) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    lastBridgedRef.current = deepLink;
    try { win.postMessage({ source: "cortex-app-route", hash: deepLink }, window.location.origin); } catch {}
  }, [deepLink]);

  // Loader gate: cover the iframe until the view's React root mounts (same
  // detection as before — a fixed-position [id$="-root"] / [data-view-root]).
  //
  // The iframe gives us no "ready" event (the view is rendered by the vendored
  // studio inside it), so readiness is POLLED. On FAILURE the poll must not
  // simply stop: leaving viewReady false with no error spins "Opening…"
  // forever, which makes a permission failure, a crashed view and a slow load
  // indistinguishable. Mirrors SharedApp.jsx.
  useEffect(() => {
    setViewReady(false);
    setViewTimedOut(false);
    setViewDenied(null);
    if (!embedSrc) return;
    // A DEPLOYED app (portal_url) lives on its own domain: contentDocument is
    // cross-origin and unreadable, and readiness comes from the iframe's own
    // onLoad instead. Polling it would only ever hit the timeout and show a
    // false error, so don't.
    if (selected?.portal_url) return;
    let done = false;
    const started = Date.now();
    const id = setInterval(() => {
      if (done) return;
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc) {
          const candidates = [
            doc.getElementById("customers-dashboard-root"),
            ...doc.querySelectorAll('[id$="-root"]:not(#root):not(#app-root):not(#flow-root)'),
            ...doc.querySelectorAll('[data-view-root]'),
          ].filter(Boolean);
          const mounted = candidates.some((el) => {
            if (el.hasAttribute("data-view-root")) return true;
            const cs = doc.defaultView?.getComputedStyle?.(el);
            return cs && cs.position === "fixed" && el.childElementCount > 0;
          });
          if (mounted) { done = true; clearInterval(id); setViewReady(true); }
        }
      } catch { /* same-origin; ignore transient nulls */ }
      if (Date.now() - started > 90000) { done = true; clearInterval(id); setViewTimedOut(true); }
    }, 400);
    return () => { done = true; clearInterval(id); };
  }, [embedSrc, viewAttempt, selected?.portal_url]);

  // The embedded studio reports a refused socket upgrade (WS close 4401/4403)
  // to its host — see installFlowWsDenialReporter in flow-studio/mount. Naming
  // a permission failure immediately beats waiting out the 90s timeout.
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type !== "cortex:flow-ws-denied") return;
      setViewDenied(e.data.code === 4403
        ? "You don't have permission to run this app."
        : "Could not authenticate with the app service. Try signing in again.");
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const patchAppLocal = (id, patch) => setApps((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const doRename = async (app, title) => {
    setBusy(true); setDialogErr(null);
    try {
      const t = title.trim();
      await updateApp(app.id, { title: t ? t : null });
      patchAppLocal(app.id, { title: t || null, is_renamed: !!t });
      setRenameTarget(null);
    } catch (e) { setDialogErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  // Duplicate: two scopes (see DuplicateDialog). UI copy stays in this
  // project; FULL copy first creates a new project (name = the copy's title),
  // then asks the backend to clone the engine + data into it, and finally
  // jumps there — the copy is fully independent from the original.
  const projectSlugFor = (title) => {
    let s = String(title).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 48);
    if (!/^[a-z]/.test(s)) s = "p_" + s;
    return s;
  };
  const doDuplicate = async (app, title, mode) => {
    setBusy(true); setDialogErr(null);
    try {
      const t = title.trim();
      if (mode === "project") {
        const slug = projectSlugFor(t);
        await createProject({ name: t, slug, description: `Full copy of ${displayTitle(app)} — app, engine endpoints and data schema.` });
        const r = await duplicateApp(app.id, { title: t, target_project_slug: slug });
        setCopyTarget(null);
        window.location.href = `/p/${slug}/b/main/apps/${encodeURIComponent(r?.app?.view_name || app.view_name)}`;
        return;
      }
      const r = await duplicateApp(app.id, { title: t || undefined });
      setCopyTarget(null);
      if (r?.app) {
        setApps((prev) => (prev.some((a) => a.id === r.app.id) ? prev : [...prev, r.app]));
        navigate(createPageUrl(`apps/${encodeURIComponent(r.app.view_name)}`));
      } else {
        load();
      }
    } catch (e) { setDialogErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const doMove = async (app, folderId) => {
    setBusy(true); setDialogErr(null);
    try {
      await updateApp(app.id, { folder_id: folderId });
      patchAppLocal(app.id, { folder_id: folderId });
      setMoveTarget(null);
    } catch (e) { setDialogErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const doRemove = async (app) => {
    setBusy(true); setDialogErr(null);
    try {
      await removeApp(app.id);
      setApps((prev) => prev.filter((a) => a.id !== app.id));
      setRemoveTarget(null);
      if (selected?.view_name === app.view_name) navigate(createPageUrl("apps"));
    } catch (e) { setDialogErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const doFolderSave = async (name) => {
    setBusy(true); setDialogErr(null);
    try {
      const n = name.trim();
      if (!n) throw new Error("Folder name is required");
      if (folderDialog.mode === "create") {
        const f = await createAppFolder(n, folderDialog.parentId ?? null);
        setFolders((prev) => [...prev, f].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        await updateAppFolder(folderDialog.folder.id, { name: n });
        setFolders((prev) => prev.map((f) => (f.id === folderDialog.folder.id ? { ...f, name: n } : f)));
      }
      setFolderDialog(null);
    } catch (e) { setDialogErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const doFolderDelete = async (folder) => {
    setBusy(true);
    try {
      await deleteAppFolder(folder.id);
      // Contents re-parent to the folder's parent (mirror the backend).
      setFolders((prev) => prev.filter((f) => f.id !== folder.id).map((f) => (f.parent_id === folder.id ? { ...f, parent_id: folder.parent_id } : f)));
      setApps((prev) => prev.map((a) => (a.folder_id === folder.id ? { ...a, folder_id: folder.parent_id } : a)));
      setFolderDialog(null);
    } catch (e) { setDialogErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  // ── Drag & drop plumbing ───────────────────────────────────────────────────
  // Native HTML5 DnD. Every drop target (folder row, app row, the My-apps root
  // area) gets these handlers; `key` identifies the target for highlight and
  // `folderId` is where a drop files the dragged app (null = root). Handlers
  // stopPropagation so a drop on a nested target doesn't also fire on the root.
  const APP_DND_TYPE = "application/x-cortex-app";
  const dropHandlers = (key, folderId) => ({
    onDragOver: (e) => {
      if (dragAppId == null) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      if (dropTarget !== key) setDropTarget(key);
    },
    onDragLeave: () => { setDropTarget((t) => (t === key ? null : t)); },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDropTarget(null);
      const raw = (() => { try { return e.dataTransfer.getData(APP_DND_TYPE); } catch { return ""; } })();
      const id = dragAppId ?? (raw ? Number(raw) : null);
      setDragAppId(null);
      const app = apps.find((a) => a.id === id);
      if (app && app.is_owner && (app.folder_id ?? null) !== folderId) doMove(app, folderId);
    },
  });

  // ── Rail data: sections + folder tree ─────────────────────────────────────
  const myApps = apps.filter((a) => a.is_owner);
  const sharedApps = apps.filter((a) => !a.is_owner && a.shared_with_me);
  const orgApps = apps.filter((a) => !a.is_owner && !a.shared_with_me);
  const childFolders = (pid) => folders.filter((f) => (f.parent_id ?? null) === pid);
  // An app pointing at a folder that isn't in the visible tree (deleted /
  // other-org folder) falls back to root instead of disappearing.
  const knownFolder = (fid) => fid != null && folders.some((f) => f.id === fid);
  const appsInFolder = (fid) => myApps.filter((a) =>
    fid === null ? !knownFolder(a.folder_id) : a.folder_id === fid
  );

  // ── Row renderers ──────────────────────────────────────────────────────────
  const VisibilityBadge = ({ app }) => {
    if (!app.is_owner && !app.can_manage) return null;
    return app.visibility === "org"
      ? <Globe size={12} className="shrink-0 opacity-60" title="Visible to everyone in the organization" />
      : (app.shares?.length
        ? <Users size={12} className="shrink-0 opacity-60" title={`Shared with ${app.shares.length}`} />
        : <Lock size={12} className="shrink-0 opacity-60" title="Private" />);
  };

  // Managed app-template instance: maintained by the cortex system (the App
  // Templates hub); org users can open it but not modify it.
  const ManagedBadge = ({ app }) =>
    app.managed_template ? (
      <span
        data-testid={`app-managed-${app.view_name}`}
        title={`Managed app template "${app.managed_template}"${app.managed_version ? ` v${app.managed_version}` : ""} — maintained by the cortex system`}
        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-current/30 px-1.5 py-[1px] text-[10px] leading-none opacity-70"
      >
        ⬡ managed
      </span>
    ) : null;

  const AppRow = ({ app, depth = 0, droppable = false }) => {
    const active = selected && selected.view_name === app.view_name;
    const menuOpen = menuAppId === app.id;
    const dragging = dragAppId === app.id;
    return (
      <div
        key={app.id}
        draggable={app.is_owner}
        onDragStart={(e) => {
          if (!app.is_owner) return;
          setDragAppId(app.id);
          try { e.dataTransfer.setData(APP_DND_TYPE, String(app.id)); e.dataTransfer.effectAllowed = "move"; } catch {}
        }}
        onDragEnd={() => { setDragAppId(null); setDropTarget(null); }}
        // An app row inside My-apps is itself a drop target that files into ITS
        // folder (dropping onto a sibling = dropping into that sibling's folder,
        // like Drive) — and it must not fall through to the root handler.
        {...(droppable ? dropHandlers(`a${app.id}`, app.folder_id ?? null) : {})}
        className={
          "group relative w-full rounded-md text-sm flex items-center gap-1 " +
          (active ? "bg-nav-active text-white" : "text-page-text hover:bg-card-border/40") +
          (dragging ? " opacity-40" : "")
        }
        style={{ paddingLeft: depth * 14 }}
      >
        <button
          type="button"
          data-testid={`app-view-${app.view_name}`}
          onClick={() => openView(app.view_name)}
          className="flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-inherit"
        >
          {app.has_agent
            ? <span className="shrink-0 w-[15px] text-center leading-none" aria-hidden>{app.agent_icon || "🤖"}</span>
            : <AppWindow size={15} className="shrink-0 opacity-80" />}
          <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{displayTitle(app)}</span>
          <ManagedBadge app={app} />
          <VisibilityBadge app={app} />
        </button>
        {app.has_agent ? (
          <button
            type="button"
            data-testid={`app-ask-agent-${app.view_name}`}
            title="Ask this app's agent"
            onClick={(e) => { e.stopPropagation(); askAgent(app); }}
            className={
              "shrink-0 p-1 rounded cursor-pointer border-0 bg-transparent " +
              (active ? "text-white/80 hover:text-white" : "text-page-muted hover:text-nav-active opacity-0 group-hover:opacity-100")
            }
          >
            <Bot size={14} />
          </button>
        ) : (
          <button
            type="button"
            data-testid={`app-add-agent-${app.view_name}`}
            title="Add a backing agent"
            onClick={(e) => { e.stopPropagation(); addAgent(app); }}
            className={
              "shrink-0 p-1 rounded cursor-pointer border-0 bg-transparent " +
              (active ? "text-white/80 hover:text-white" : "text-page-muted hover:text-nav-active opacity-0 group-hover:opacity-100")
            }
          >
            <Bot size={14} />
          </button>
        )}
        <button
          type="button"
          data-testid={`app-view-share-${app.view_name}`}
          title="Open in new tab"
          onClick={(e) => { e.stopPropagation(); openInNewTab(app); }}
          className={
            "shrink-0 p-1 rounded cursor-pointer border-0 bg-transparent " +
            (active ? "text-white/80 hover:text-white" : "text-page-muted hover:text-page-text opacity-0 group-hover:opacity-100")
          }
        >
          <ExternalLink size={13} />
        </button>
        {canBuild && (<button
          type="button"
          data-testid={`app-menu-${app.view_name}`}
          title="More"
          onClick={(e) => { e.stopPropagation(); setMenuFolderId(null); setMenuAppId(menuOpen ? null : app.id); }}
          className={
            "shrink-0 mr-1 p-1 rounded cursor-pointer border-0 bg-transparent " +
            (active ? "text-white/80 hover:text-white" : "text-page-muted hover:text-page-text opacity-0 group-hover:opacity-100 " + (menuOpen ? "opacity-100" : ""))
          }
        >
          <MoreVertical size={14} />
        </button>)}
        {menuOpen && (
          <div
            className="absolute right-1 top-full z-40 mt-1 w-48 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text"
            onClick={(e) => e.stopPropagation()}
          >
            {app.has_agent
              ? <MenuItem icon={Bot} label="Ask agent" onClick={() => { setMenuAppId(null); askAgent(app); }} />
              : <MenuItem icon={Bot} label="Add agent" onClick={() => { setMenuAppId(null); addAgent(app); }} />}
            <MenuItem icon={ExternalLink} label="Open in new tab" onClick={() => { setMenuAppId(null); openInNewTab(app); }} />
            <MenuItem icon={LinkIcon} label="Copy link" onClick={() => { setMenuAppId(null); try { navigator.clipboard.writeText(shareLink(app)); } catch {} }} />
            <MenuItem icon={Share2} label="Share…" onClick={() => { setMenuAppId(null); setDialogErr(null); setShareTarget(app); }} />
            {app.can_manage && <MenuItem icon={Eye} label="Viewer options…" onClick={() => { setMenuAppId(null); setDialogErr(null); setViewerOptionsTarget(app); }} />}
            {app.can_manage && <MenuItem icon={Pencil} label="Rename" onClick={() => { setMenuAppId(null); setDialogErr(null); setRenameTarget(app); }} />}
            {app.is_owner && folders.length > 0 && <MenuItem icon={Folder} label="Move to folder…" onClick={() => { setMenuAppId(null); setDialogErr(null); setMoveTarget(app); }} />}
            <MenuItem icon={Copy} label="Duplicate…" onClick={() => { setMenuAppId(null); setDialogErr(null); setCopyTarget(app); }} />
            {isPlatformAdmin && !app.managed_template && (
              <MenuItem icon={Package} label="Publish as template…" onClick={() => { setMenuAppId(null); setDialogErr(null); setPublishTarget(app); }} />
            )}
            <MenuItem icon={Wrench} label="Build a change" onClick={() => { setMenuAppId(null); setRequestDialog({ kind: "change", app }); }} />
            {/* Reachable WITHOUT opening the app: a broken view never renders its
                header, and "what changed recently" is exactly the question you
                have when an app stops working. Sits next to Report a bug for
                that reason. */}
            <MenuItem icon={History} label="Version history" onClick={() => { setMenuAppId(null); setHistoryTarget(app); }} />
            <MenuItem icon={Bug} label="Report a bug" onClick={() => { setMenuAppId(null); setRequestDialog({ kind: "bug_fix", app }); }} />
            {app.can_manage && <MenuItem icon={Trash2} label="Remove" danger onClick={() => { setMenuAppId(null); setDialogErr(null); setRemoveTarget(app); }} />}
          </div>
        )}
      </div>
    );
  };

  const FolderRow = ({ folder, depth }) => {
    const isCollapsed = collapsed.has(folder.id);
    const menuOpen = menuFolderId === folder.id;
    return (
      <div key={`f${folder.id}`}>
        <div
          {...dropHandlers(`f${folder.id}`, folder.id)}
          className={
            "group relative w-full rounded-md text-sm flex items-center gap-1 text-page-text hover:bg-card-border/40" +
            (dropTarget === `f${folder.id}` ? " ring-1 ring-nav-active bg-nav-active/10" : "")
          }
          style={{ paddingLeft: depth * 14 }}
        >
          <button
            type="button"
            data-testid={`app-folder-${folder.id}`}
            onClick={() => toggleFolder(folder.id)}
            className="flex-1 min-w-0 text-left px-2 py-1.5 flex items-center gap-1.5 cursor-pointer border-0 bg-transparent text-inherit"
          >
            {isCollapsed ? <ChevronRight size={13} className="shrink-0 opacity-60" /> : <ChevronDown size={13} className="shrink-0 opacity-60" />}
            {isCollapsed ? <Folder size={14} className="shrink-0 opacity-80" /> : <FolderOpen size={14} className="shrink-0 opacity-80" />}
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{folder.name}</span>
          </button>
          <button
            type="button"
            title="Folder actions"
            onClick={(e) => { e.stopPropagation(); setMenuAppId(null); setMenuFolderId(menuOpen ? null : folder.id); }}
            className="shrink-0 mr-1 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-page-text opacity-0 group-hover:opacity-100"
          >
            <MoreVertical size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-1 top-full z-40 mt-1 w-44 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text" onClick={(e) => e.stopPropagation()}>
              <MenuItem icon={FolderPlus} label="New subfolder" onClick={() => { setMenuFolderId(null); setDialogErr(null); setFolderDialog({ mode: "create", parentId: folder.id }); }} />
              <MenuItem icon={Pencil} label="Rename" onClick={() => { setMenuFolderId(null); setDialogErr(null); setFolderDialog({ mode: "rename", folder }); }} />
              <MenuItem icon={Trash2} label="Delete folder" danger onClick={() => { setMenuFolderId(null); doFolderDelete(folder); }} />
            </div>
          )}
        </div>
        {!isCollapsed && (
          <div>
            {childFolders(folder.id).map((f) => <FolderRow key={`f${f.id}`} folder={f} depth={depth + 1} />)}
            {appsInFolder(folder.id).map((a) => <AppRow key={a.id} app={a} depth={depth + 1} droppable />)}
          </div>
        )}
      </div>
    );
  };

  const Section = ({ label, action, children, empty }) => (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1 flex items-center">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{label}</span>
        {action}
      </div>
      {children}
      {empty}
    </div>
  );

  return (
    <div className="flex h-full min-h-0">
      {!railOpen && (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button
            type="button"
            data-testid="apps-rail-open"
            onClick={toggleRail}
            title="Show apps list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftOpen size={18} />
          </button>
          <AppWindow size={16} className="text-nav-active mt-1" />
        </div>
      )}
      {/* Left rail: the Drive-like apps explorer (collapsible) */}
      <aside data-tour="apps-rail" className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <AppWindow size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Apps</span>
          <button
            type="button"
            data-testid="apps-requests"
            onClick={() => navigate(createPageUrl("board"))}
            title="Board"
            className="ml-auto relative text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <ClipboardList size={15} />
            {openRequests > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-nav-active text-white text-[9px] leading-[14px] text-center">
                {openRequests}
              </span>
            )}
          </button>
          <button
            type="button"
            data-testid="apps-new-folder"
            onClick={() => { setDialogErr(null); setFolderDialog({ mode: "create", parentId: null }); }}
            title="New folder"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <FolderPlus size={15} />
          </button>
          <button
            type="button"
            onClick={load}
            title="Refresh"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <RefreshCw size={14} />
          </button>
          <button
            type="button"
            data-testid="apps-rail-close"
            onClick={toggleRail}
            title="Collapse apps list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
              <Loader2 size={16} className="animate-spin" /> Loading apps…
            </div>
          )}
          {!loading && err && <div className="text-sm text-red-500 px-2 py-3">{err}</div>}
          {!loading && !err && apps.length === 0 && folders.length === 0 && (
            <div className="text-sm text-page-muted px-2 py-3" data-testid="apps-empty-state">
              {isPlatformAdmin && projectSlug === "app_templates" ? (
                <>
                  This is the App Templates <span className="font-medium">library</span> — published
                  templates and their versions live under <span className="font-medium">Templates</span>,
                  not here. To author a new template: build an app as usual in any project whose data
                  model becomes the template&apos;s contract, then use{" "}
                  <span className="font-medium">&quot;Publish as template&quot;</span> from the
                  app&apos;s menu.
                </>
              ) : (
                <>No apps yet. Build a dashboard or a <code>*.view</code> endpoint in Flow.</>
              )}
            </div>
          )}
          {!loading && !err && (myApps.length > 0 || folders.length > 0) && (
            <Section label="My apps">
              {/* Root drop zone: dropping here (outside any folder row) unfiles
                  the app back to the My-apps root. */}
              <div
                data-testid="my-apps-drop-root"
                {...dropHandlers("root", null)}
                className={"rounded-md" + (dropTarget === "root" ? " ring-1 ring-nav-active bg-nav-active/5" : "")}
                style={{ minHeight: 34 }}
              >
                {childFolders(null).map((f) => <FolderRow key={`f${f.id}`} folder={f} depth={0} />)}
                {appsInFolder(null).map((a) => <AppRow key={a.id} app={a} depth={0} droppable />)}
              </div>
            </Section>
          )}
          {!loading && !err && sharedApps.length > 0 && (
            <Section label="Shared with me">
              {sharedApps.map((a) => <AppRow key={a.id} app={a} depth={0} />)}
            </Section>
          )}
          {!loading && !err && orgApps.length > 0 && (
            <Section label="Organization">
              {orgApps.map((a) => <AppRow key={a.id} app={a} depth={0} />)}
            </Section>
          )}
        </div>
        {/* Rail footer: file a request for a new app (agent-built). */}
        {canBuild && (
        <div className="p-2 border-t border-card-border">
          <button
            type="button"
            data-testid="apps-request-new"
            onClick={() => setRequestDialog({ kind: "new_app" })}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer"
          >
            <Sparkles size={14} /> Build an app
          </button>
        </div>
        )}
      </aside>

      {/* Main: render the selected view */}
      <main data-tour="app-embed" className="flex-1 min-w-0 min-h-0 relative bg-page-bg">
        {!selected && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <AppWindow size={40} className="text-page-muted mb-3" />
            <div className="text-page-text font-medium">Select an app</div>
            <div className="text-sm text-page-muted mt-1">
              Pick an app from the left to open it here.
            </div>
          </div>
        )}
        {selected && denied && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6 bg-page-bg">
            <Lock size={36} className="text-page-muted mb-3" />
            <div className="text-page-text font-medium">You don't have access to this app</div>
            <div className="text-sm text-page-muted mt-1 max-w-md">
              Ask the app's owner to share it with you or make it available to the organization.
            </div>
          </div>
        )}
        {selected && !denied && !flowProjectId && !loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-page-muted">
            No Flow project linked to this cortex project.
          </div>
        )}
        {embedSrc && (
          <>
            {/* Header: title + owner + Share for the open view */}
            <div className="absolute top-0 inset-x-0 h-12 z-20 flex items-center gap-3 px-4 border-b border-card-border bg-card-bg">
              <span className="font-semibold text-page-text truncate">{displayTitle(selected)}</span>
              {selected.owner && !selected.is_owner && (
                <span className="hidden sm:inline-flex items-center gap-1 text-xs text-page-muted truncate">
                  <UserRound size={12} /> {selected.owner.name || selected.owner.email}
                </span>
              )}
              <button
                type="button"
                title="Open in new tab"
                onClick={() => selected.id && openInNewTab(selected)}
                className="ml-auto inline-flex items-center gap-1.5 text-sm text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
              >
                <ExternalLink size={15} />
              </button>
              <button
                type="button"
                data-testid="app-view-share-header"
                onClick={() => {
                  if (selected?.portal_url) {
                    // Copy the BARE link (recipients sign in themselves); open
                    // our own tab with the session handed off.
                    // Portal: the shareable link IS the app's own domain.
                    navigator.clipboard?.writeText(selected.portal_url).catch(() => {});
                    window.open(portalHandoff(selected.portal_url), "_blank", "noopener,noreferrer");
                    return;
                  }
                  if (selected.id) { setDialogErr(null); setShareTarget(selected); }
                }}
                title={selected?.portal_url ? `Copy + open ${selected.portal_url}` : "Share"}
                className="inline-flex items-center gap-1.5 text-sm text-nav-active hover:text-nav-active-hover bg-transparent border-0 cursor-pointer p-1"
              >
                <Share2 size={15} /> Share
              </button>
            </div>
            <iframe
              // viewAttempt makes Try again a real remount, not just a re-poll.
              key={`${embedKey}#${viewAttempt}`}
              ref={iframeRef}
              data-testid="app-view-iframe"
              src={embedSrc}
              onLoad={() => { if (selected?.portal_url) setViewReady(true); }}
              title={displayTitle(selected)}
              style={{ position: "absolute", top: 48, left: 0, right: 0, bottom: 0, width: "100%", height: "calc(100% - 48px)", border: 0 }}
            />
            {!viewReady && (
              <div
                style={{ position: "absolute", top: 48, left: 0, right: 0, bottom: 0, zIndex: 10 }}
                className="flex flex-col items-center justify-center gap-3 bg-page-bg px-6 text-center">
                {viewDenied || viewTimedOut ? (
                  <>
                    <AlertTriangle size={30} className="text-amber-600" />
                    <div className="text-base font-semibold text-page-title">
                      {viewDenied ? "You can't open this app" : "This app didn't finish loading"}
                    </div>
                    <div className="text-sm text-page-muted max-w-[460px]">
                      {viewDenied || "It may still be starting up, or you may not have permission to run it. Try again, and contact the app's owner if it keeps failing."}
                    </div>
                    <button
                      onClick={() => setViewAttempt((n) => n + 1)}
                      className="mt-1 h-9 px-4 rounded-[5px] border-0 cursor-pointer bg-nav-active text-white text-sm font-medium hover:bg-nav-active-dark transition-colors"
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  <>
                    <Loader2 size={28} className="animate-spin text-nav-active" />
                    <div className="text-sm text-page-muted">Opening {displayTitle(selected)}…</div>
                  </>
                )}
              </div>
            )}
            {/* The companion chat bubble AND the standalone Workflows circle
                are gone: with an app open, the speed dial (AssistantChat,
                bottom-right) is the ONE control — Chat, Workflows
                (data-driven), Pipeline, Governance. */}
          </>
        )}
      </main>

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
      {viewerOptionsTarget && (
        <ViewerOptionsDialog
          app={viewerOptionsTarget}
          projectSlug={projectSlug}
          link={shareLink(viewerOptionsTarget)}
          displayTitle={displayTitle}
          onClose={() => setViewerOptionsTarget(null)}
          onSave={async (chrome) => {
            await updateApp(viewerOptionsTarget.id, { viewer_chrome: chrome });
            patchAppLocal(viewerOptionsTarget.id, { viewer_chrome: chrome });
            setViewerOptionsTarget(null);
          }}
        />
      )}
      {renameTarget && (
        <RenameDialog
          app={renameTarget}
          busy={busy}
          error={dialogErr}
          displayTitle={displayTitle}
          onClose={() => setRenameTarget(null)}
          onSave={(t) => doRename(renameTarget, t)}
        />
      )}
      {publishTarget && (
        <PublishTemplateDialog
          app={publishTarget}
          projectSlug={projectSlug}
          displayTitle={displayTitle}
          onClose={() => setPublishTarget(null)}
        />
      )}
      {copyTarget && (
        <DuplicateDialog
          app={copyTarget}
          busy={busy}
          error={dialogErr}
          displayTitle={displayTitle}
          onClose={() => setCopyTarget(null)}
          onSave={(t, mode) => doDuplicate(copyTarget, t, mode)}
        />
      )}
      {moveTarget && (
        <MoveDialog
          app={moveTarget}
          folders={folders}
          busy={busy}
          error={dialogErr}
          onClose={() => setMoveTarget(null)}
          onMove={(fid) => doMove(moveTarget, fid)}
        />
      )}
      {removeTarget && (
        <Modal title="Remove app" onClose={() => setRemoveTarget(null)}>
          <div className="text-sm text-page-text mb-4">
            Remove <b>{displayTitle(removeTarget)}</b> from Apps? People it was shared with will lose
            access. This does not touch the data or pipelines behind it.
          </div>
          {dialogErr && <div className="text-sm text-red-500 mb-3">{dialogErr}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} onClick={() => setRemoveTarget(null)}>Cancel</button>
            <button type="button" data-testid="app-remove-confirm" className={btnPrimary + " bg-red-600"} disabled={busy} onClick={() => doRemove(removeTarget)}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remove
            </button>
          </div>
        </Modal>
      )}
      {folderDialog && (
        <FolderDialog
          dialog={folderDialog}
          busy={busy}
          error={dialogErr}
          onClose={() => setFolderDialog(null)}
          onSave={doFolderSave}
        />
      )}
      {requestDialog && (
        <AppRequestDialog
          kinds={APP_REQUEST_KINDS}
          targets={apps.map((a) => ({ id: a.id, label: displayTitle(a) }))}
          targetField="app_id"
          preset={{
            kind: requestDialog.kind,
            title: requestDialog.title,
            details: requestDialog.details,
            targetId: requestDialog.app?.id ?? null,
          }}
          onClose={() => setRequestDialog(null)}
          onCreated={() => { setRequestDialog(null); navigate(createPageUrl("board")); }}
        />
      )}
      {historyTarget && (
        <Modal title={`Version history — ${displayTitle(historyTarget)}`} width={560} onClose={() => setHistoryTarget(null)}>
          <VersionHistory kind="app" name={historyTarget.view_name}
                          canRestore={!!historyTarget.can_manage}
                          onRestored={() => setHistoryTarget(null)} />
        </Modal>
      )}
      {shareTarget && (
        <ShareDialog
          app={apps.find((a) => a.id === shareTarget.id) || shareTarget}
          me={me}
          link={shareLink(shareTarget)}
          onClose={() => setShareTarget(null)}
          onVisibility={async (v) => {
            await updateApp(shareTarget.id, { visibility: v });
            patchAppLocal(shareTarget.id, { visibility: v });
          }}
          onShare={async (email) => {
            const r = await shareApp(shareTarget.id, email);
            patchAppLocal(shareTarget.id, {
              shares: [...(apps.find((a) => a.id === shareTarget.id)?.shares || []), { user_id: r.user_id, email: r.email, name: null }],
            });
          }}
          onUnshare={async (userId) => {
            await unshareApp(shareTarget.id, userId);
            patchAppLocal(shareTarget.id, {
              shares: (apps.find((a) => a.id === shareTarget.id)?.shares || []).filter((s) => s.user_id !== userId),
            });
          }}
        />
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 cursor-pointer border-0 bg-transparent " +
        (danger ? "text-red-500 hover:bg-red-500/10" : "text-page-text hover:bg-card-border/40")
      }
    >
      <Icon size={14} className="shrink-0 opacity-80" /> {label}
    </button>
  );
}

// Viewer options — the owner curates this app's COMPANIONS: which buttons the
// speed dial offers (chat included — on by default, but the owner can share
// the bare app).
//
// These checkboxes govern EVERY surface identically — the in-cortex app view, a
// share link (signed-in or not) and a deployment all render the list
// GET /api/apps/resolve computes from exactly these values
// (shared/viewer-companions.js). There is no longer a scope caveat: a companion
// checked here appears for every viewer who can open the app, and its backing
// route serves them (the docs/workflows proxies allow share-only READS).
//
// AND THEY ARE NO LONGER PURE PRESENTATION. The same values compose the CHAT's
// persona roster on both live-cortex app surfaces: the chat companion is the
// cortex orchestrator, and the assistant reads this column server-side to decide
// which personas it carries — Workflows OFF ⇒ the Workflow Assistant is absent
// from its delegates; Pipeline OFF ⇒ it cannot run the pipeline; Governance ON ⇒
// the data-dictionary companion AND the read side of the governance persona.
// Absent, not refused (assistant/companion-gate.js).
//
// WRITE access is its own, separate tick: `governance_authoring` (the sub-toggle
// under Governance) is what lets a LINK-HOLDER — an app_user with no project
// access of their own — author the recipe through this chat. Default FALSE
// everywhere; ticking it is the owner deliberately publishing that capability on
// one app. Members and admins are unaffected either way: their rights come from
// project membership, not from this door.
function ViewerOptionsDialog({ app, projectSlug, link, displayTitle, onClose, onSave }) {
  const saved = { ...VIEWER_CHROME_DEFAULTS, ...(app.viewer_chrome || {}) };
  const [chrome, setChrome] = useState({
    chat: saved.chat,
    workflows: saved.workflows, guides: saved.guides,
    pipeline: saved.pipeline, governance: saved.governance, docs: saved.docs,
  });
  const [wfCount, setWfCount] = useState(null);   // null = loading
  const [guides, setGuides] = useState(null);     // null = loading; [] = none
  const [picked, setPicked] = useState(null);     // Set of guide slugs (null until guides load)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    wfEngine("list_definitions", {})
      .then((r) => { if (alive) setWfCount((r?.definitions || []).length); })
      .catch(() => { if (alive) setWfCount(0); });
    loadProjectGuides(projectSlug)
      .then((tours) => {
        if (!alive) return;
        // ONLY the guides authored for THIS app (tour.app === view name).
        // Project-wide guides tour cortex chrome — pages that do not exist on
        // the chrome-free share surface — so offering them here would only
        // let the owner attach broken tours.
        const list = (tours || [])
          .filter((t) => t?.__guide?.app === app.view_name)
          .map((t) => ({ slug: t?.__guide?.slug, title: t.title || t?.__guide?.slug }))
          .filter((g) => g.slug);
        setGuides(list);
        // Saved per-guide picks win; the default is all of this app's guides —
        // the same rule the share page applies with no config.
        const savedPicks = Array.isArray(app.viewer_chrome?.guide_slugs) ? app.viewer_chrome.guide_slugs : null;
        setPicked(new Set(savedPicks ?? list.map((g) => g.slug)));
      })
      .catch(() => { if (alive) { setGuides([]); setPicked(new Set()); } });
    return () => { alive = false; };
  }, [projectSlug, app.view_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (k) => setChrome((c) => ({ ...c, [k]: !c[k] }));
  const toggleGuide = (slug) => setPicked((p) => {
    const n = new Set(p); if (n.has(slug)) n.delete(slug); else n.add(slug); return n;
  });

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const out = { ...chrome };
      if (picked && guides) {
        const defaults = guides.map((g) => g.slug);
        const same = defaults.length === picked.size && defaults.every((s) => picked.has(s));
        if (!same) out.guide_slugs = [...picked];
      }
      const isAllDefault = !out.guide_slugs
        && Object.keys(VIEWER_CHROME_DEFAULTS).every((k) => out[k] === VIEWER_CHROME_DEFAULTS[k]);
      await onSave(isAllDefault ? null : out);
    } catch (e) { setError(e?.message || String(e)); setBusy(false); }
  };

  // The render rule, mirrored EXACTLY: the checkboxes and nothing else. This
  // preview is a promise to the owner about what viewers get, so it has to be
  // the same function every dial runs (shared/viewer-companions.js) — it used
  // to additionally require `wfCount > 0` / a non-empty guide pick, which made
  // it disagree with the surfaces it claims to predict. Data availability is
  // shown as a per-row `note` below instead: worth telling the owner, never a
  // reason to silently drop a companion they switched on.
  const viewerSees = [
    ...(chrome.chat ? ["Chat"] : []),
    ...(chrome.workflows ? ["Workflows"] : []),
    ...(chrome.guides ? ["Guides"] : []),
    ...(chrome.pipeline ? ["Pipeline"] : []),
    ...(chrome.governance ? ["Governance"] : []),
    ...(chrome.docs ? ["Docs"] : []),
  ];

  const Row = ({ k, icon: Icon, label, badge, note, always }) => (
    <label className={"flex items-start gap-2.5 py-2 " + (always ? "opacity-80" : "cursor-pointer")}>
      <input
        type="checkbox"
        data-testid={`viewer-opt-${k}`}
        className="mt-0.5"
        checked={always ? true : chrome[k]}
        disabled={!!always}
        onChange={() => toggle(k)}
      />
      <Icon size={15} className="mt-0.5 shrink-0 text-page-muted" />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 text-sm text-page-text">{label} {badge}</span>
        {note && <span className="block text-xs text-page-muted mt-0.5">{note}</span>}
      </span>
    </label>
  );

  return (
    <Modal title={`Viewer options — ${displayTitle(app)}`} onClose={onClose} width={560}>
      {/* Share link, so the owner sees exactly WHAT page they are curating. */}
      <div className="flex items-center gap-2 mb-3">
        <input readOnly className={inputCls + " font-mono text-xs"} value={link} onFocus={(e) => e.target.select()} />
        <button type="button" className={btnGhost + " shrink-0"} data-testid="viewer-opt-copy-link"
          onClick={() => { try { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} }}>
          {copied ? <Check size={14} /> : <LinkIcon size={14} />} {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="divide-y divide-card-border/60">
        <Row k="chat" icon={MessageSquare} label="Chat"
          note="The Cortex chat — it answers questions about this app's data and, when you tick the surfaces below, does the work behind them too (Governance lets it author processing rules and mappings; Workflows lets it reach the Workflow Assistant). On by default; untick it to share the bare app." />
        <Row k="workflows" icon={Workflow} label="Workflows"
          note={wfCount == null ? "checking…" : wfCount > 0
            ? `${wfCount} workflow${wfCount === 1 ? "" : "s"} in this project`
            : "no workflows in this project yet — the button will open an empty list"} />
        <Row k="guides" icon={GraduationCap} label="Guides"
          note={guides == null ? "checking…" : guides.length === 0
            ? "no saved guides for this app yet — the button will open an empty list"
            : null} />
        {chrome.guides && guides && guides.length > 0 && picked && (
          <div className="pl-9 pb-2 max-h-40 overflow-y-auto">
            {guides.map((g) => (
              <label key={g.slug} className="flex items-center gap-2 py-1 text-sm text-page-text cursor-pointer">
                <input type="checkbox" data-testid={`viewer-opt-guide-${g.slug}`}
                  checked={picked.has(g.slug)} onChange={() => toggleGuide(g.slug)} />
                <span className="truncate">{g.title}</span>
              </label>
            ))}
          </div>
        )}
        <Row k="pipeline" icon={GitBranch} label="Pipeline"
          note="The pipeline canvas, read over the app — and lets the chat run the pipeline." />
        <Row k="governance" icon={ShieldCheck} label="Governance"
          note="Data dictionary — elements, sources, mappings, rules. Also lets the chat CHANGE them: with this on, a viewer can ask it to add a processing rule and it will." />
        <Row k="docs" icon={BookText} label="Docs"
          note="Specification and knowledge base." />
      </div>

      {/* What EVERY viewer gets, on EVERY surface — the in-cortex app view, a
          share link, a deployment. One list, no per-surface caveat. */}
      <div className="mt-3 rounded-md border border-card-border bg-page-bg px-3 py-2 text-xs" data-testid="viewer-opt-preview">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-page-muted w-28 shrink-0">Every viewer sees:</span>
          {viewerSees.map((c) => <span key={c} className="px-1.5 py-0.5 rounded bg-nav-active/10 text-page-text">{c}</span>)}
          {viewerSees.length === 0 && <span className="text-page-muted italic">just the app</span>}
        </div>
      </div>

      {error && <div className="text-sm text-red-500 mt-2">{error}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
        <button type="button" data-testid="viewer-opt-save" className={btnPrimary} disabled={busy} onClick={save}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
        </button>
      </div>
    </Modal>
  );
}

function RenameDialog({ app, busy, error, displayTitle, onClose, onSave }) {
  const [value, setValue] = useState(displayTitle(app));
  return (
    <Modal title="Rename app" onClose={onClose}>
      <input
        autoFocus
        data-testid="app-rename-input"
        className={inputCls}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && value.trim()) onSave(value); }}
      />
      {error && <div className="text-sm text-red-500 mt-2">{error}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
        <button type="button" data-testid="app-rename-save" className={btnPrimary} disabled={busy || !value.trim()} onClick={() => onSave(value)}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
        </button>
      </div>
    </Modal>
  );
}

// Name-the-copy dialog with a SCOPE choice. Both copies are private to the
// caller and never touch the original app:
//   ui      — a new view on the same fork; it reads the project's data through
//             the same engine endpoints. Safe for interface experiments.
//   project — a NEW project: engine endpoints copied, data cloned into its own
//             schema. Fully independent — engine changes can't reach the
//             original. (Requires the right to create projects: org admin.)
// Publish an app into the App Templates library (docs/app-templates.md §4.2:
// templates are authored as real apps — ideally in an App Templates org
// authoring project, whose recipe is the template's test data model — then
// published; the contract is drafted from the source project's recipe).
function PublishTemplateDialog({ app, projectSlug, displayTitle, onClose }) {
  const suggested = (displayTitle(app) || app.view_name)
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "template";
  const [slug, setSlug] = useState(suggested);
  const [version, setVersion] = useState("1.0.0");
  const [title, setTitle] = useState(displayTitle(app) || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // { contractElements }
  const publish = async () => {
    setBusy(true); setError(null);
    try {
      const r = await exportTemplate(projectSlug, app.view_name, { template_slug: slug, version, title: title || undefined });
      setDone({ contractElements: (r.contract?.entities || []).flatMap((e) => e.elements || []).length });
    } catch (e) { setError(e?.message || "publish failed"); }
    setBusy(false);
  };
  return (
    <Modal title="Publish as template" onClose={onClose}>
      {done ? (
        <div className="text-sm text-page-text" data-testid="tpl-publish-done">
          <p className="mb-2">
            Published <span className="font-mono">{slug}</span> v{version} to the App Templates library
            {done.contractElements > 0 ? <> — contract drafted from this project&apos;s data model ({done.contractElements} element{done.contractElements === 1 ? "" : "s"})</> : " with an empty data contract"}.
          </p>
          <p className="text-page-muted text-xs mb-3">Manage installs from the Templates panel in the App Templates organization.</p>
          <div className="flex justify-end"><button type="button" className={btnPrimary} onClick={onClose}>Done</button></div>
        </div>
      ) : (
        <>
          <label className="block text-xs text-page-muted mb-1">Template slug</label>
          <input data-testid="tpl-publish-slug" className={inputCls} value={slug} onChange={(e) => setSlug(e.target.value)} />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs text-page-muted mb-1">Version</label>
              <input data-testid="tpl-publish-version" className={inputCls} value={version} onChange={(e) => setVersion(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-page-muted mb-1">Title</label>
              <input data-testid="tpl-publish-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-page-muted mt-3">
            The data contract is drafted from THIS project&apos;s governed data model — install into another
            organization is gated on that model being satisfied there.
          </p>
          {error && <div className="text-sm text-red-500 mt-2">{error}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
            <button type="button" data-testid="tpl-publish-save" className={btnPrimary} disabled={busy || !slug.trim() || !/^\d+\.\d+\.\d+$/.test(version)} onClick={publish}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />} Publish
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function DuplicateDialog({ app, busy, error, displayTitle, onClose, onSave }) {
  const [value, setValue] = useState(`${displayTitle(app)} (copy)`);
  const [mode, setMode] = useState("ui");
  const opt = (m, title, desc) => (
    <label
      className={"flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer " +
        (mode === m ? "border-nav-active bg-nav-active/5" : "border-card-border hover:bg-card-border/20")}
    >
      <input type="radio" name="dup-scope" className="mt-1" checked={mode === m} onChange={() => setMode(m)} />
      <span>
        <span className="block text-sm text-page-text font-medium">{title}</span>
        <span className="block text-xs text-page-muted mt-0.5">{desc}</span>
      </span>
    </label>
  );
  return (
    <Modal title="Duplicate app" onClose={onClose}>
      <div className="text-sm text-page-muted mb-3">
        Creates your own private copy of <b className="text-page-text">{displayTitle(app)}</b>.
        The original stays untouched either way.
      </div>
      <div className="space-y-2 mb-3">
        {opt("ui", "Interface only (same data)",
          "New app in this project. It shows the SAME live data through the same engine — perfect for changing the UI safely.")}
        {opt("project", "Full copy (own project & data)",
          "New project with copies of the engine endpoints and a cloned data schema. Fully independent — nothing you change here can affect the original.")}
      </div>
      <input
        autoFocus
        data-testid="app-duplicate-input"
        className={inputCls}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && value.trim() && !busy) onSave(value, mode); }}
      />
      {mode === "project" && (
        <div className="text-xs text-page-muted mt-1.5">
          A new project named after the title above will be created (org admins only). Copying the data can take a minute.
        </div>
      )}
      {error && <div className="text-sm text-red-500 mt-2">{error}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
        <button type="button" data-testid="app-duplicate-save" className={btnPrimary} disabled={busy || !value.trim()} onClick={() => onSave(value, mode)}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Duplicate
        </button>
      </div>
    </Modal>
  );
}

function MoveDialog({ app, folders, busy, error, onClose, onMove }) {
  // Flatten the folder tree with depth for indentation.
  const flat = [];
  const walk = (pid, depth) => {
    for (const f of folders.filter((x) => (x.parent_id ?? null) === pid)) {
      flat.push({ ...f, depth });
      walk(f.id, depth + 1);
    }
  };
  walk(null, 0);
  const current = app.folder_id ?? null;
  return (
    <Modal title="Move to folder" onClose={onClose}>
      <div className="max-h-72 overflow-y-auto -mx-1">
        <FolderChoice label="My apps (no folder)" depth={0} root selected={current === null} disabled={busy} onPick={() => onMove(null)} />
        {flat.map((f) => (
          <FolderChoice key={f.id} label={f.name} depth={f.depth} selected={current === f.id} disabled={busy} onPick={() => onMove(f.id)} />
        ))}
      </div>
      {error && <div className="text-sm text-red-500 mt-2">{error}</div>}
    </Modal>
  );
}

function FolderChoice({ label, depth, root, selected, disabled, onPick }) {
  return (
    <button
      type="button"
      disabled={disabled || selected}
      onClick={onPick}
      className={
        "w-full text-left px-3 py-2 text-sm flex items-center gap-2 rounded-md cursor-pointer border-0 bg-transparent " +
        (selected ? "text-nav-active" : "text-page-text hover:bg-card-border/40")
      }
      style={{ paddingLeft: 12 + depth * 16 }}
    >
      {root ? <AppWindow size={14} className="opacity-70" /> : <Folder size={14} className="opacity-70" />}
      <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
      {selected && <Check size={14} className="ml-auto" />}
    </button>
  );
}

function FolderDialog({ dialog, busy, error, onClose, onSave }) {
  const [value, setValue] = useState(dialog.mode === "rename" ? dialog.folder.name : "");
  return (
    <Modal title={dialog.mode === "create" ? "New folder" : "Rename folder"} onClose={onClose}>
      <input
        autoFocus
        data-testid="app-folder-name-input"
        className={inputCls}
        placeholder="Folder name"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && value.trim()) onSave(value); }}
      />
      {error && <div className="text-sm text-red-500 mt-2">{error}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
        <button type="button" data-testid="app-folder-save" className={btnPrimary} disabled={busy || !value.trim()} onClick={() => onSave(value)}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
        </button>
      </div>
    </Modal>
  );
}

// Drive-style share dialog: copy link, org-visibility toggle, people list.
// Non-managers get a read-only version (link + who owns it).
function ShareDialog({ app, me, link, onClose, onVisibility, onShare, onUnshare }) {
  const [email, setEmail] = useState("");
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const canManage = !!app.can_manage;

  useEffect(() => {
    if (!canManage) return;
    listOrgMembers().then((r) => setMembers(r?.members || [])).catch(() => {});
  }, [canManage]);

  const run = async (fn) => {
    setPending(true); setErr(null);
    try { await fn(); } catch (e) { setErr(e?.message || String(e)); } finally { setPending(false); }
  };

  const copy = () => {
    try { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  const candidates = members.filter(
    (m) => m.id !== me?.id && !(app.shares || []).some((s) => s.user_id === m.id)
  );

  return (
    <Modal title={`Share “${app.title || app.view_name}”`} onClose={onClose} width={480}>
      {/* Link */}
      <div className="flex items-center gap-2 mb-4">
        <input readOnly className={inputCls + " flex-1 text-page-muted"} value={link} onFocus={(e) => e.target.select()} />
        <button type="button" data-testid="app-share-copy-link" className={btnGhost + " shrink-0"} onClick={copy}>
          {copied ? <Check size={14} /> : <LinkIcon size={14} />} {copied ? "Copied" : "Copy link"}
        </button>
      </div>

      {/* Visibility */}
      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-page-muted mb-2">General access</div>
        {canManage ? (
          <div className="space-y-1.5">
            <VisibilityChoice
              icon={Lock}
              label="Private"
              hint="Only you and people you share with can open it"
              active={app.visibility === "private"}
              disabled={pending}
              onPick={() => run(() => onVisibility("private"))}
            />
            <VisibilityChoice
              icon={Globe}
              label="Anyone in the organization with the link"
              hint="Every member of your organization can open it"
              active={app.visibility === "org"}
              disabled={pending}
              onPick={() => run(() => onVisibility("org"))}
            />
          </div>
        ) : (
          <div className="text-sm text-page-text flex items-center gap-2">
            {app.visibility === "org" ? <Globe size={14} /> : <Lock size={14} />}
            {app.visibility === "org" ? "Anyone in the organization with the link" : "Private"}
          </div>
        )}
      </div>

      {/* People */}
      {canManage && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-page-muted mb-2">People with access</div>
          <div className="flex items-center gap-2 mb-2">
            <input
              data-testid="app-share-email-input"
              className={inputCls + " flex-1"}
              placeholder="Add a member by email…"
              value={email}
              list="app-share-members"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email.trim()) run(async () => { await onShare(email.trim()); setEmail(""); });
              }}
            />
            <datalist id="app-share-members">
              {candidates.map((m) => <option key={m.id} value={m.email}>{m.name || m.email}</option>)}
            </datalist>
            <button
              type="button"
              data-testid="app-share-add"
              className={btnPrimary + " shrink-0"}
              disabled={pending || !email.trim()}
              onClick={() => run(async () => { await onShare(email.trim()); setEmail(""); })}
            >
              {pending ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />} Add
            </button>
          </div>
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {app.owner && (
              <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-page-text">
                <UserRound size={14} className="opacity-70" />
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{app.owner.name || app.owner.email || "—"}</span>
                <span className="ml-auto text-xs text-page-muted">Owner</span>
              </div>
            )}
            {(app.shares || []).map((s) => (
              <div key={s.user_id} className="flex items-center gap-2 px-2 py-1.5 text-sm text-page-text rounded-md hover:bg-card-border/30">
                <UserRound size={14} className="opacity-70" />
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{s.name || s.email}</span>
                <button
                  type="button"
                  data-testid={`app-unshare-${s.user_id}`}
                  title="Remove access"
                  disabled={pending}
                  onClick={() => run(() => onUnshare(s.user_id))}
                  className="ml-auto p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-red-500"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {(app.shares || []).length === 0 && (
              <div className="text-sm text-page-muted px-2 py-1.5">Not shared with anyone yet.</div>
            )}
          </div>
        </div>
      )}
      {err && <div className="text-sm text-red-500 mt-3">{err}</div>}
    </Modal>
  );
}

// ── App requests ─────────────────────────────────────────────────────────────
// Kind labels/icons live in the shared Board meta module; the request LIST
// itself is the Board page (/board).

// Kind metadata for the shared AppRequestDialog on the Apps page. change /
// bug_fix target an existing app (needsTarget → app_id); new_app takes none.
const APP_REQUEST_KINDS = [
  { kind: "new_app", label: "New app", icon: Sparkles, hint: REQUEST_KIND_META.new_app.hint,
    tenancyChoice: true, suggestFeatures: true,
    titlePlaceholder: "What should the app be called / do?",
    detailsPlaceholder: "Describe what you need in plain language — data to show, actions, who it's for…" },
  { kind: "change", label: "Change an app", icon: Wrench, hint: REQUEST_KIND_META.change.hint,
    needsTarget: true, targetLabel: "Select an app…", titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe what you need in plain language — data to show, actions, who it's for…" },
  { kind: "bug_fix", label: "Bug fix", icon: Bug, hint: REQUEST_KIND_META.bug_fix.hint,
    needsTarget: true, targetLabel: "Select an app…", titlePlaceholder: "Short summary",
    detailsPlaceholder: "What's broken? What did you expect to happen?" },
];

function VisibilityChoice({ icon: Icon, label, hint, active, disabled, onPick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      className={
        "w-full text-left px-3 py-2 rounded-md flex items-start gap-2.5 cursor-pointer border " +
        (active ? "border-nav-active bg-nav-active/10" : "border-card-border bg-transparent hover:bg-card-border/30")
      }
    >
      <Icon size={15} className={"mt-0.5 shrink-0 " + (active ? "text-nav-active" : "text-page-muted")} />
      <span className="min-w-0">
        <span className={"block text-sm " + (active ? "text-nav-active font-medium" : "text-page-text")}>{label}</span>
        <span className="block text-xs text-page-muted">{hint}</span>
      </span>
      {active && <Check size={15} className="ml-auto mt-0.5 text-nav-active" />}
    </button>
  );
}
